import { DropdownMenu, SkeletonBlock, SkeletonProvider, SkeletonText } from '@protoss/ui';
import * as React from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { ResizableDivider } from '../ResizableDivider';

// ─── Side-panel context ───────────────────────────────────────────────────────

interface MainContentSidePanelContextValue {
  setPanel: (node: React.ReactNode | null) => void;
}

/**
 * Allows child content rendered inside MainContentShell to project a panel
 * into the shell's flex layout, making it a full-height sibling of the
 * header + body column rather than nested inside the scrollable content area.
 */
export const MainContentSidePanelContext =
  React.createContext<MainContentSidePanelContextValue | null>(null);

// ─── Header actions context ───────────────────────────────────────────────────

interface MainContentHeaderActionsContextValue {
  setHeaderActions: (node: React.ReactNode | null) => void;
}

/**
 * Allows child content rendered inside MainContentShell to project action
 * buttons into the shell header's right side.
 */
export const MainContentHeaderActionsContext =
  React.createContext<MainContentHeaderActionsContextValue | null>(null);

const MAX_BOTTOM_HEIGHT_RATIO = 0.6; // 60% of container height
const MIN_BOTTOM_HEIGHT = 120;
const DEFAULT_BOTTOM_HEIGHT = 240;

export interface MainContentShellProps {
  /** Primary page title (used for document/page title and as fallback) */
  title: string;

  /** Optional React node rendered inside the title h1 instead of the title string */
  titleNode?: React.ReactNode;

  /** Optional content rendered inline after the title h1 (e.g. status pills) */
  titleMeta?: React.ReactNode;

  /** Optional breadcrumb text displayed below title */
  breadcrumb?: string;

  /** Optional muted subtitle rendered inline after the title on the same line */
  subtitle?: string;

  /** Whether user can edit this entity (enables Edit menu) */
  canEdit?: boolean;

  /** Callback when Edit is clicked */
  onEdit?: () => void;

  /** Optional callback when Delete is clicked (shows in edit menu) */
  onDelete?: () => void;

  /** Optional additional actions rendered on the right side of header */
  actions?: React.ReactNode;

  /** Main content area */
  children: React.ReactNode;

  /** Switches the shell subtree into skeleton mode while preserving layout. */
  loading?: boolean;

  /** Optional bottom pane content */
  bottomPaneContent?: React.ReactNode;

  /** Whether to show the bottom pane (default: false) */
  bottomPaneVisible?: boolean;

  /** Optional full-width content rendered between the header and the scrollable body (e.g. tab nav) */
  subnav?: React.ReactNode;
}

/**
 * MainContentShell - reusable page shell for main pane content
 *
 * Provides a standardized layout structure for domain/project detail pages
 * with header, content area, and optional resizable bottom pane.
 *
 * Layout structure:
 * ┌─────────────────────────────────────────┐
 * │ Title          │          Actions       │
 * │ Breadcrumb     │                        │
 * ├─────────────────────────────────────────┤
 * │                                         │
 * │         Main Content (children)         │
 * │                                         │
 * ├─────────────────────────────────────────┤ ← Resizable divider (optional)
 * │         Bottom Pane (optional)          │
 * └─────────────────────────────────────────┘
 *
 * Features:
 * - Consistent header layout with title, breadcrumb, and actions
 * - Scrollable content area
 * - Optional resizable bottom pane (hidden by default)
 * - Token-based styling following design system
 *
 * Example usage:
 * ```tsx
 * // Domain detail page
 * <MainContentShell
 *   title="Engineering"
 *   breadcrumb="Domain"
 *   canEdit={isWorkspaceAdmin}
 *   onEdit={handleEditDomain}
 * >
 *   <DomainContent domain={domain} />
 * </MainContentShell>
 *
 * // Project detail page
 * <MainContentShell
 *   title="CMS Platform"
 *   breadcrumb="Engineering > CMS Platform"
 *   canEdit={isWorkspaceAdmin}
 *   onEdit={handleEditProject}
 *   bottomPaneContent={<ActivityLog />}
 *   bottomPaneVisible={showActivity}
 * >
 *   <ProjectContent project={project} />
 * </MainContentShell>
 * ```
 */
export function MainContentShell({
  title,
  titleNode,
  titleMeta,
  breadcrumb,
  subtitle,
  canEdit = false,
  onEdit,
  onDelete,
  actions,
  children,
  loading = false,
  bottomPaneContent,
  bottomPaneVisible = false,
  subnav,
}: MainContentShellProps) {
  usePageTitle(title);

  const [bottomHeight, setBottomHeight] = React.useState(DEFAULT_BOTTOM_HEIGHT);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleResize = React.useCallback(
    (delta: number) => {
      if (!containerRef.current) return;

      const containerHeight = containerRef.current.clientHeight;
      const maxHeight = containerHeight * MAX_BOTTOM_HEIGHT_RATIO;
      const newHeight = Math.max(MIN_BOTTOM_HEIGHT, Math.min(maxHeight, bottomHeight - delta));

      setBottomHeight(newHeight);
    },
    [bottomHeight]
  );

  const [sidePanel, setSidePanel] = React.useState<React.ReactNode>(null);
  const sidePanelCtxValue = React.useMemo<MainContentSidePanelContextValue>(
    () => ({ setPanel: setSidePanel }),
    []
  );

  const [projectedHeaderActions, setProjectedHeaderActions] = React.useState<React.ReactNode>(null);
  const headerActionsCtxValue = React.useMemo<MainContentHeaderActionsContextValue>(
    () => ({ setHeaderActions: setProjectedHeaderActions }),
    []
  );

  const showBottom = bottomPaneVisible && bottomPaneContent;
  const showEditMenu = canEdit && (onEdit || onDelete);
  const showHeaderRight = showEditMenu || actions || projectedHeaderActions;

  return (
    <MainContentHeaderActionsContext.Provider value={headerActionsCtxValue}>
      <MainContentSidePanelContext.Provider value={sidePanelCtxValue}>
        <SkeletonProvider loading={loading}>
          <div className="main-content-shell">
            {sidePanel}
            <div className="main-content-shell__column">
              {/* Header: title, breadcrumb, actions */}
              <div className="main-content-shell__header">
                <div className="main-content-shell__header-left">
                  <div className="main-content-shell__title-row">
                    <h1 className="main-content-shell__title">
                      {titleNode ?? (
                        <>
                          <SkeletonText>{title}</SkeletonText>
                          {subtitle && (
                            <span className="main-content-shell__title-sub">
                              <SkeletonText>{subtitle}</SkeletonText>
                            </span>
                          )}
                        </>
                      )}
                    </h1>
                    {titleMeta}
                  </div>
                  {breadcrumb && (
                    <div className="main-content-shell__breadcrumb">
                      <SkeletonText>{breadcrumb}</SkeletonText>
                    </div>
                  )}
                </div>
                {showHeaderRight && (
                  <div className="main-content-shell__header-right">
                    {loading ? (
                      <>
                        <SkeletonBlock className="main-content-shell__header-action-skeleton" />
                        {showEditMenu ? (
                          <SkeletonBlock className="main-content-shell__icon-action-skeleton" />
                        ) : null}
                      </>
                    ) : (
                      <>
                        {projectedHeaderActions}
                        {actions}
                        {showEditMenu && (
                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild>
                              <button
                                type="button"
                                className="entity-action-button"
                                aria-label="Entity actions"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                >
                                  <circle cx="8" cy="3" r="1.5" fill="currentColor" />
                                  <circle cx="8" cy="8" r="1.5" fill="currentColor" />
                                  <circle cx="8" cy="13" r="1.5" fill="currentColor" />
                                </svg>
                              </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                              <DropdownMenu.Content
                                className="entity-action-menu dropdown-content"
                                side="bottom"
                                align="end"
                                sideOffset={4}
                              >
                                {onEdit && (
                                  <DropdownMenu.Item
                                    className="entity-action-menu-item dropdown-item"
                                    onSelect={onEdit}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                      aria-hidden="true"
                                    >
                                      <path
                                        d="M11 2l3 3-8 8H3v-3l8-8z"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    Edit
                                  </DropdownMenu.Item>
                                )}
                                {onDelete && (
                                  <DropdownMenu.Item
                                    className="entity-action-menu-item dropdown-item entity-action-menu-item--danger"
                                    onSelect={onDelete}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                      aria-hidden="true"
                                    >
                                      <path
                                        d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4l1 9a1 1 0 001 1h4a1 1 0 001-1l1-9"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    Delete
                                  </DropdownMenu.Item>
                                )}
                              </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Optional subnav strip - between header and scrollable body */}
              {subnav && <div className="main-content-shell__subnav">{subnav}</div>}

              {/* Body: main content + optional bottom pane */}
              <div className="main-content-shell__body" ref={containerRef}>
                <div className="main-content-shell__content">{children}</div>

                {showBottom && (
                  <>
                    <ResizableDivider
                      direction="horizontal"
                      onResize={handleResize}
                      ariaLabel="Resize bottom pane"
                    />
                    <div
                      className="main-content-shell__bottom"
                      style={{ height: `${bottomHeight}px` }}
                    >
                      {bottomPaneContent}
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* end main-content-shell__column */}
          </div>
        </SkeletonProvider>
      </MainContentSidePanelContext.Provider>
    </MainContentHeaderActionsContext.Provider>
  );
}
